import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Grid,
  Divider,
  Chip,
  Popover
} from '@mui/material';
import { useQuery, gql } from '@apollo/client';
import { styled } from '@mui/material/styles';
import { SketchPicker } from 'react-color';

// Import UserRole enum for role selection
import { UserRole, UserRoleLabels } from '../utils/enums';
import { GET_CHECKLISTS } from '../graphql/checklist';

// Override Material UI default styles
const StyledDialog = styled(Dialog)({
  '& .MuiPaper-root': {
    fontFamily: 'inherit',
  },
  '& .MuiTypography-root': {
    fontFamily: 'inherit',
  },
  '& .MuiInputBase-root': {
    fontFamily: 'inherit',
  },
  '& .MuiFormLabel-root': {
    fontFamily: 'inherit',
  },
  '& .MuiButton-root': {
    fontFamily: 'inherit',
  }
});

export const TimeBaseType = {
  CHECKIN: 'CHECKIN',
  CHECKOUT: 'CHECKOUT',
  CUSTOM: 'CUSTOM'
};

// Define job categories enum
export const JobCategory = {
  CLEANING: 'CLEANING',
  INSPECTION: 'INSPECTION',
  WALKTHROUGH: 'WALKTHROUGH',
  MAINTENANCE: 'MAINTENANCE',
  CUSTOM: 'CUSTOM'
};

// Add labels for job categories
export const JobCategoryLabels = {
  CLEANING: 'Cleaning',
  INSPECTION: 'Inspection',
  WALKTHROUGH: 'Walkthrough',
  MAINTENANCE: 'Maintenance',
  CUSTOM: 'Custom'
};

// Define department options
export const Department = {
  HOUSEKEEPING: 'HOUSEKEEPING',
  PROPERTY_MANAGEMENT: 'PROPERTY_MANAGEMENT',
};

export const DepartmentLabels = {
  HOUSEKEEPING: 'Housekeeping',
  PROPERTY_MANAGEMENT: 'Property Management',
};


export default function ServiceTypeModal({ isOpen, onClose, onSubmit, serviceType }) {
  const [formData, setFormData] = useState({
    title: '',
    timeBase: TimeBaseType.CUSTOM,
    color: '#3B82F6', // Default blue color
    defaultStartTime: '09:00 AM', // Changed from defaultTime
    requiresChecklist: false,
    requiresCheckIn: false,
    checklistId: '',
    canSubmitChecklist: false, // Add new field for can submit checklist
    allowedRoles: Object.values(UserRole), // Default to all available roles in our subset
    category: JobCategory.CLEANING, // Default category
    showFinancialFields: false,
    usePropertyFinancialFields: false,
    useChecklistStyle: false,
    departments: Object.values(Department),
  });

  // State for color picker
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const isColorPickerOpen = Boolean(colorPickerAnchor);

  // Replace categoriesData query with checklistData
  const { data: checklistData, refetch: refetchChecklists, loading: checklistsLoading } = useQuery(GET_CHECKLISTS, {
    fetchPolicy: 'cache-and-network', // Always check network for fresh data
    notifyOnNetworkStatusChange: true
  });

  // Memoize checklistOptions to prevent unnecessary re-renders
  const checklistOptions = useMemo(() => checklistData?.checklists || [], [checklistData?.checklists]);

  // Track the serviceType ID we've initialized for to prevent re-initialization
  const initializedServiceTypeId = useRef(null);

  // Refetch checklists when modal opens to ensure we have the latest data
  useEffect(() => {
    if (isOpen && refetchChecklists) {
      refetchChecklists();
    }
  }, [isOpen, refetchChecklists]);

  // Reset formData when modal opens/closes or serviceType ID changes (not when checklistOptions changes)
  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      initializedServiceTypeId.current = null;
      setFormData({
        title: '',
        timeBase: TimeBaseType.CUSTOM,
        color: '#3B82F6',
        defaultStartTime: '09:00 AM',
        requiresChecklist: false,
        requiresCheckIn: false,
        checklistId: '',
        canSubmitChecklist: false,
        allowedRoles: Object.values(UserRole),
        category: JobCategory.CLEANING,
        showFinancialFields: false,
        usePropertyFinancialFields: false,
        useChecklistStyle: false,
        departments: Object.values(Department),
      });
      return;
    }

    // Get the current serviceType ID (or null for new service types)
    const currentServiceTypeId = serviceType?.id || 'new';
    
    // Only initialize if we haven't already initialized for this serviceType
    if (initializedServiceTypeId.current === currentServiceTypeId) {
      return;
    }

    // Mark as initialized
    initializedServiceTypeId.current = currentServiceTypeId;

    if (serviceType) {
      // Filter allowedRoles to only include roles from our subset
      // Handle null, undefined, or empty array cases
      let filteredAllowedRoles = Object.values(UserRole); // Default to all roles
      if (serviceType.allowedRoles && Array.isArray(serviceType.allowedRoles)) {
        // If it's an empty array, preserve it (means no roles allowed)
        if (serviceType.allowedRoles.length === 0) {
          filteredAllowedRoles = [];
        } else {
          filteredAllowedRoles = serviceType.allowedRoles.filter(role => Object.values(UserRole).includes(role));
          // Only default to all roles if the original array had items but filtering removed them all
          // (This shouldn't happen, but handle edge case)
          if (filteredAllowedRoles.length === 0 && serviceType.allowedRoles.length > 0) {
            filteredAllowedRoles = Object.values(UserRole);
          }
        }
      }
      
      // Only set checklistId if checklists are loaded and the ID is valid
      let checklistId = '';
      if (!checklistsLoading && checklistOptions.length > 0 && serviceType.checklistId) {
        const isValidChecklistId = checklistOptions.some(checklist => checklist.id === serviceType.checklistId);
        if (isValidChecklistId) {
          checklistId = serviceType.checklistId;
        }
      } else if (serviceType.checklistId && !checklistsLoading) {
        // If checklists are loaded but the ID is not found, use empty string
        checklistId = '';
      } else if (serviceType.checklistId && checklistsLoading) {
        // If still loading, keep the original ID for now
        checklistId = serviceType.checklistId;
      }
        
      setFormData({
        title: serviceType.title || '',
        timeBase: serviceType.timeBase || TimeBaseType.CUSTOM,
        color: serviceType.color || '#3B82F6',
        defaultStartTime: serviceType.defaultStartTime || serviceType.defaultTime || '09:00 AM',
        requiresChecklist: serviceType.requiresChecklist || false,
        requiresCheckIn: serviceType.requiresCheckIn || false,
        checklistId: checklistId,
        canSubmitChecklist: (serviceType.canSubmitChecklist ?? serviceType.requiresChecklist) || false,
        allowedRoles: filteredAllowedRoles,
        category: serviceType.category || JobCategory.CLEANING,
        showFinancialFields: serviceType.showFinancialFields || false,
        usePropertyFinancialFields: serviceType.usePropertyFinancialFields || false,
        useChecklistStyle: serviceType.useChecklistStyle || false,
        departments: Array.isArray(serviceType.departments) && serviceType.departments.length > 0
          ? serviceType.departments
          : Object.values(Department),
      });
    } else {
      // Reset to defaults when creating new service type
      setFormData({
        title: '',
        timeBase: TimeBaseType.CUSTOM,
        color: '#3B82F6',
        defaultStartTime: '09:00 AM',
        requiresChecklist: false,
        requiresCheckIn: false,
        checklistId: '',
        canSubmitChecklist: false,
        allowedRoles: Object.values(UserRole),
        category: JobCategory.CLEANING,
        showFinancialFields: false,
        usePropertyFinancialFields: false,
        useChecklistStyle: false,
        departments: Object.values(Department),
      });
    }
  }, [isOpen, serviceType?.id, checklistsLoading]); // Only depend on serviceType.id, not the whole object

  // Update checklistId separately when checklists finish loading (only if it's invalid)
  useEffect(() => {
    if (!checklistsLoading && checklistOptions.length > 0 && formData.checklistId) {
      const isValidChecklistId = checklistOptions.some(checklist => checklist.id === formData.checklistId);
      if (!isValidChecklistId) {
        setFormData(prev => ({ ...prev, checklistId: '' }));
      }
    }
  }, [checklistsLoading, checklistOptions, formData.checklistId]);

  // Add time validation function
  const validateTimeFormat = (timeString) => {
    const timeRegex = /^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM|am|pm)$/;
    return timeRegex.test(timeString);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // For built-in types (Cleaning, Walkthrough, Pre-arrival Inspection)
    if (serviceType?.isBuiltIn) {
      // Only send the exact fields allowed for built-in types
      const builtInUpdate = {
        requiresChecklist: Boolean(formData.requiresChecklist),
        checklistId: formData.requiresChecklist ? formData.checklistId : null,
        canSubmitChecklist: Boolean(formData.canSubmitChecklist),
        allowedRoles: formData.allowedRoles,
        showFinancialFields: Boolean(formData.showFinancialFields),
        usePropertyFinancialFields: Boolean(formData.usePropertyFinancialFields),
        departments: formData.departments,
        useChecklistStyle: Boolean(formData.useChecklistStyle),
      };

      try {
        onSubmit(builtInUpdate);
      } catch (error) {
        console.error('ServiceTypeModal - Error in onSubmit:', error);
        throw error;
      }
    } else {
      // For custom types, send all fields
      const customUpdate = {
        title: formData.title,
        timeBase: formData.timeBase,
        color: formData.color,
        defaultTime: formData.defaultStartTime || '09:00 AM',
        requiresChecklist: Boolean(formData.requiresChecklist),
        requiresCheckIn: Boolean(formData.requiresCheckIn),
        checklistId: formData.requiresChecklist ? formData.checklistId : null,
        canSubmitChecklist: Boolean(formData.canSubmitChecklist),
        allowedRoles: formData.allowedRoles,
        category: formData.category,
        showFinancialFields: Boolean(formData.showFinancialFields),
        usePropertyFinancialFields: Boolean(formData.usePropertyFinancialFields),
        departments: formData.departments,
        useChecklistStyle: Boolean(formData.useChecklistStyle),
      };
      onSubmit(customUpdate);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    
    // Add time validation
    if (name === 'defaultStartTime') {
      if (value !== '' && !validateTimeFormat(value)) {
        e.target.setCustomValidity('Please enter time in format: HH:MM AM/PM (e.g., 09:00 AM)');
      } else {
        e.target.setCustomValidity('');
      }
    }

    setFormData(prev => {
      const updates = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      
      // Special handling for requiresChecklist
      if (name === 'requiresChecklist') {
        if (checked) {
          // When requiresChecklist is checked, default canSubmitChecklist to true
          updates.canSubmitChecklist = true;
        } else {
          // When requiresChecklist is unchecked, reset canSubmitChecklist and checklistId
          updates.canSubmitChecklist = false;
          updates.checklistId = '';
          updates.useChecklistStyle = false;
        }
      }
      
      return updates;
    });
  };

  // Handle color picker change
  const handleColorChange = (color) => {
    setFormData(prev => ({
      ...prev,
      color: color.hex
    }));
  };

  // Handle opening color picker
  const handleColorPickerOpen = (event) => {
    if (!isBuiltIn) {
      setColorPickerAnchor(event.currentTarget);
    }
  };

  // Handle closing color picker
  const handleColorPickerClose = () => {
    setColorPickerAnchor(null);
  };

  const isBuiltIn = serviceType?.isBuiltIn;



  return (
    <StyledDialog 
      open={isOpen} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: 'font-sans'
      }}
    >
      <DialogTitle className="text-xl font-medium border-b pb-4">
        {serviceType ? 'Edit Service Type' : 'Add Service Type'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent className="py-6">
          {isBuiltIn && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                This is a built-in service type. You can assign or remove a checklist for this service type.
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            <TextField
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              required
              disabled={isBuiltIn}
              className="font-sans"
              InputProps={{
                className: 'font-sans'
              }}
              InputLabelProps={{
                className: 'font-sans'
              }}
            />

            {/* Department Selection */}
            <div className="space-y-2">
              <Divider />
              <Typography variant="subtitle1" className="font-medium">
                Department
              </Typography>
              <Typography variant="body2" className="text-gray-500">
                Assign this service type to one or both departments
              </Typography>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                {Object.entries(Department).map(([key, value]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={formData.departments.includes(value)}
                        onChange={(e) => {
                          const { checked } = e.target;
                          setFormData(prev => ({
                            ...prev,
                            departments: checked
                              ? [...prev.departments, value]
                              : prev.departments.filter(d => d !== value)
                          }));
                        }}
                        name={`department-${value}`}
                      />
                    }
                    label={DepartmentLabels[key]}
                    className="font-sans"
                  />
                ))}
              </div>
              {formData.departments.length === 0 && (
                <Typography variant="body2" className="text-amber-600">
                  Warning: No department selected. This service type won't appear in any department.
                </Typography>
              )}
              {formData.departments.length > 0 && (
                <Box className="flex flex-wrap gap-1">
                  {formData.departments.map(dept => (
                    <Chip
                      key={dept}
                      label={DepartmentLabels[dept] || dept}
                      onDelete={() => {
                        setFormData(prev => ({
                          ...prev,
                          departments: prev.departments.filter(d => d !== dept)
                        }));
                      }}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
              <Divider />
            </div>

            {/* Add Category dropdown */}
            <FormControl fullWidth required>
              <InputLabel className="font-sans">Category</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category"
                disabled={isBuiltIn}
                className="font-sans"
              >
                {Object.entries(JobCategory).map(([key, value]) => (
                  <MenuItem key={key} value={value} className="font-sans">
                    {JobCategoryLabels[key] || key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel className="font-sans">Time Base</InputLabel>
              <Select
                name="timeBase"
                value={formData.timeBase}
                onChange={handleChange}
                label="Time Base"
                disabled={isBuiltIn}
                className="font-sans"
              >
                {Object.entries(TimeBaseType).map(([key, value]) => (
                  <MenuItem key={key} value={value} className="font-sans">
                    {key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Default Start Time"
              name="defaultStartTime"
              value={formData.defaultStartTime}
              onChange={handleChange}
              fullWidth
              required
              disabled={isBuiltIn}
              placeholder="09:00 AM"
              helperText="Enter time in format: HH:MM AM/PM (e.g., 09:00 AM)"
              className="font-sans"
              InputProps={{
                className: 'font-sans'
              }}
              InputLabelProps={{
                className: 'font-sans'
              }}
            />

            {/* Color Picker Section */}
            <div className="space-y-2">
              <Typography variant="body2" className="font-medium text-gray-700">
                Service Color
              </Typography>
              <div className="flex items-center space-x-3">
                <div
                  onClick={handleColorPickerOpen}
                  className={`w-12 h-12 rounded-lg border-2 border-gray-300 cursor-pointer shadow-sm hover:shadow-md transition-shadow ${
                    isBuiltIn ? 'cursor-not-allowed opacity-50' : 'hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: formData.color }}
                  role="button"
                  tabIndex={0}
                  aria-label="Select color"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleColorPickerOpen(e);
                    }
                  }}
                />
                <div className="flex-1">
                  <TextField
                    label="Hex Color Code"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    fullWidth
                    required
                    disabled={isBuiltIn}
                    inputProps={{
                      pattern: '^#[0-9A-Fa-f]{6}$'
                    }}
                    helperText={isBuiltIn ? "Color cannot be changed for built-in service types" : "Click the color box to use color picker or enter hex code"}
                    className="font-sans"
                    InputProps={{
                      className: 'font-sans'
                    }}
                    InputLabelProps={{
                      className: 'font-sans'
                    }}
                  />
                </div>
              </div>
              
              {/* Color Picker Popover */}
              <Popover
                open={isColorPickerOpen}
                anchorEl={colorPickerAnchor}
                onClose={handleColorPickerClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  style: { padding: '8px' }
                }}
              >
                <SketchPicker
                  color={formData.color}
                  onChange={handleColorChange}
                  disableAlpha={true}
                  presetColors={[
                    '#3B82F6', // Blue (default)
                    '#EF4444', // Red
                    '#10B981', // Green
                    '#F59E0B', // Yellow
                    '#8B5CF6', // Purple
                    '#06B6D4', // Cyan
                    '#F97316', // Orange
                    '#EC4899', // Pink
                    '#84CC16', // Lime
                    '#6366F1', // Indigo
                    '#14B8A6', // Teal
                    '#F43F5E', // Rose
                  ]}
                />
              </Popover>
            </div>

            {/* Checkbox group with responsive layout */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.requiresChecklist}
                    onChange={handleChange}
                    name="requiresChecklist"
                  />
                }
                label="Requires Checklist"
                className="font-sans"
              />
              
              {formData.requiresChecklist && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.canSubmitChecklist}
                      onChange={handleChange}
                      name="canSubmitChecklist"
                    />
                  }
                  label="Can Submit Checklist"
                  className="font-sans"
                />
              )}
              {formData.requiresChecklist && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.useChecklistStyle}
                      onChange={handleChange}
                      name="useChecklistStyle"
                    />
                  }
                  label="Checklist Style"
                  className="font-sans"
                />
              )}
            </div>

            {formData.requiresChecklist && formData.useChecklistStyle && (
              <Typography variant="body2" className="text-gray-500 -mt-2 mb-2">
                Use checkbox checklist instead of pass/fail scoring
              </Typography>
            )}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.showFinancialFields}
                  onChange={handleChange}
                  name="showFinancialFields"
                />
              }
              label="Show Financial Fields"
              className="font-sans"
            />

            {/* Add Use Property Financial Fields checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.usePropertyFinancialFields}
                  onChange={handleChange}
                  name="usePropertyFinancialFields"
                />
              }
              label="Use Property Financial Fields"
              className="font-sans"
            />
  </div>
            {formData.requiresChecklist && (
              <FormControl fullWidth required>
                <div className="flex items-center gap-2 mb-2">
                  <InputLabel className="font-sans">Select Checklist</InputLabel>
                  <button
                    type="button"
                    onClick={() => {
                      refetchChecklists();
                    }}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    title="Refresh checklist list"
                  >
                    🔄 Refresh
                  </button>
                </div>
                <Select
                  name="checklistId"
                  value={checklistsLoading ? '' : formData.checklistId}
                  onChange={handleChange}
                  label="Select Checklist"
                  className="font-sans"
                  disabled={checklistsLoading}
                >
                  {checklistsLoading ? (
                    <MenuItem disabled className="font-sans">
                      Loading checklists...
                    </MenuItem>
                  ) : checklistOptions.length > 0 ? (
                    checklistOptions.map((checklist) => (
                      <MenuItem 
                        key={checklist.id} 
                        value={checklist.id}
                        className="font-sans"
                      >
                        {checklist.title} ({checklist.items?.length || 0} items)
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled className="font-sans">
                      No checklists available - Create one in the Checklists tab
                    </MenuItem>
                  )}
                </Select>
                <div className="mt-2 text-sm">
                  {formData.checklistId ? (
                    <span className="text-green-600">
                      ✓ Checklist assigned: {checklistOptions.find(c => c.id === formData.checklistId)?.title}
                    </span>
                  ) : (
                    <span className="text-amber-600">
                      ⚠ No checklist assigned
                    </span>
                  )}
                </div>
              </FormControl>
            )}

            {formData.requiresChecklist && checklistOptions.length === 0 && (
              <div className="text-sm text-amber-600">
                No checklists available. Please create a checklist first in the Checklist Manager.
              </div>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.requiresCheckIn}
                  onChange={handleChange}
                  name="requiresCheckIn"
                  disabled={isBuiltIn}
                />
              }
              label="Requires Check-in"
              className="font-sans"
            />

            {/* Role Selection Section */}
            <Divider className="my-4" />
            <Typography variant="subtitle1" className="font-medium mb-2">
              Available Roles
            </Typography>
            <Typography variant="body2" className="text-gray-500 mb-3">
              Select which user roles are allowed to be assigned to this service type
            </Typography>
            
            <Grid container spacing={2}>
              {Object.entries(UserRole).map(([key, value]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.allowedRoles.includes(value)}
                        onChange={(e) => {
                          const { checked } = e.target;
                          setFormData(prev => ({
                            ...prev,
                            allowedRoles: checked
                              ? [...prev.allowedRoles, value]
                              : prev.allowedRoles.filter(role => role !== value)
                          }));
                        }}
                        name={`role-${value}`}
                      />
                    }
                    label={UserRoleLabels[value] || key}
                    className="font-sans"
                  />
                </Grid>
              ))}
            </Grid>
            
            {/* Display selected roles as chips */}
            {formData.allowedRoles.length > 0 ? (
              <Box className="mt-3 flex flex-wrap gap-1">
                {formData.allowedRoles.map(role => (
                  <Chip 
                    key={role}
                    label={UserRoleLabels[role] || role}
                    onDelete={() => {
                      setFormData(prev => ({
                        ...prev,
                        allowedRoles: prev.allowedRoles.filter(r => r !== role)
                      }));
                    }}
                    size="small"
                    className="m-1"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" className="mt-2 text-amber-600">
                Warning: No roles selected. This service type won't be assignable to any user.
              </Typography>
            )}
          </div>
        </DialogContent>

        <DialogActions className="border-t p-4">
          <Button 
            onClick={onClose}
            className="font-sans text-gray-600 hover:text-gray-800"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={formData.requiresChecklist && !formData.checklistId}
            className="font-sans bg-blue-600 hover:bg-blue-700"
          >
            {serviceType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </StyledDialog>
  );
} 